import { message } from 'antd';
import moment from 'moment';
import { Form as FormRFF, Field as FieldRFF } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';

import { QUERY_KEYS } from 'api/certificates/queries';
import { CertificateHistoryModel, PUBLICATION_MODE } from 'api/documents/documents.model';
import Button from 'components/Buttons';
import Form from 'components/Form';
import RadioGroup from 'components/Form/RadioGroup';
import { RadioOption } from 'components/Form/RadioGroup/RadioGroup';
import Upload from 'components/Form/Upload';
import Modal from 'components/Modal';
import SectionTitle from 'components/SectionTitle';
import { useAddPublicDocumentMutation } from 'hooks/useAddPublicDocumentMutation';
import { useAuthenticatedContext } from 'hooks/useAuthenticated';
import useCategoriesQuery from 'hooks/useCategoriesQuery';
import useValidationSchema from 'hooks/useValidationSchema';
import { DocumentInfoModel, CATEGORY_TYPE } from 'models/documents.model';

import * as Styled from './CreateDocumentModal.styles';
import { useSchema } from './schema';

export interface CreateRelatedDocumentModalProps {
  category: CATEGORY_TYPE;
  history: CertificateHistoryModel[];
  options: RadioOption[];
  onCloseModal: () => void;
}

const CreateDocumentModal = ({
  category,
  history,
  options,
  onCloseModal,
}: CreateRelatedDocumentModalProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const schema = useSchema();
  const { id } = useParams();
  const { currentUser } = useAuthenticatedContext();
  const { data: categories } = useCategoriesQuery();

  const validate = useValidationSchema(schema);

  const { mutate: addCertificate, isLoading } = useAddPublicDocumentMutation({
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEYS.GET_CERTIFICATES, id]);
      message.success(t('success.newCert'));
      onCloseModal();
    },
    onError: () => {
      message.error(t('error.newCert'));
    },
  });

  const handleSubmit = (values: any) => {
    const categoryId = categories?.[category];

    if (!categoryId) {
      return;
    }

    const currentDate = moment().utc();
    const validSince = currentDate.format('YYYY-MM-DD[T]HH:mm:ss');
    const validUntil = currentDate.add(10, 'y').format('YYYY-MM-DD[T]HH:mm:ss');

    const documentInfo: DocumentInfoModel = {
      title: values.title,
      categoryId,
      draft: false,
      publicationMode: PUBLICATION_MODE.UPDATED,
      validSince,
      validUntil,
      retainUntil: validUntil,
      previousDocumentBlockchainAddress: history[0].documentBlockchainAddress,
      additionalDetails: {
        transferDate: validSince,
        company: currentUser?.name || '',
      },
    };

    addCertificate({ documentInfo, documentContent: values.additionalCertificate });
  };

  return (
    <Modal onCancel={onCloseModal} visible={true} footer={null}>
      <SectionTitle>{t('certificate.addRelatedDocument')}</SectionTitle>
      <FormRFF<any>
        onSubmit={handleSubmit}
        validate={validate}
        initialValues={{ title: '' }}
        mutators={{
          setFile: ([file], state, utils) => {
            utils.changeValue(state, 'additionalCertificate', () => file);
          },
        }}
      >
        {({ handleSubmit, form, values }) => (
          <Form onSubmit={handleSubmit} data-cy="form">
            <FieldRFF name="title" type="radio" required component={RadioGroup} options={options} />
            {values.title && (
              <FieldRFF
                name="additionalCertificate"
                required
                onUploadFile={form.mutators.setFile}
                component={Upload}
                accept="application/pdf"
              />
            )}
            <Styled.ModalFooter>
              <Button key="back" size="large" onClick={onCloseModal}>
                {t('certificate.cancel')}
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                data-cy="form_submit"
                loading={isLoading}
                disabled={isLoading}
              >
                {t('certificate.addRelatedDocument')}
              </Button>
            </Styled.ModalFooter>
          </Form>
        )}
      </FormRFF>
    </Modal>
  );
};

export default CreateDocumentModal;
